import React from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { FaCheck } from 'react-icons/fa';
import { useMutation } from '@apollo/client';
import { FormInput, qglReleaseRoute, RouteLineInput } from './graphql';
import { promiseModalDialog, toastSuccess } from 'motion-components';
import { useHandleError } from 'shared-pages/services';
import { useTranslation } from 'react-i18next';
import * as Lo from 'lodash';

interface RouteCompleteButtonProps {
  route: FormInput;
  disabled?: boolean;
}

const RouteCompleteButton: React.FC<RouteCompleteButtonProps> = ({ route, disabled }) => {
  const { t } = useTranslation();
  const { handleGraphqlError } = useHandleError('route');
  const [updateRoute, { loading }] = useMutation(qglReleaseRoute);

  const handleCompleteRoute = async () => {
    /* 1️⃣ Ask for confirmation first */
    const confirmed = await promiseModalDialog({
      title: t('Ολοκλήρωση Δρομολογίου'),
      text: t(
        'Θέλετε να ολοκληρωθεί το δρομολόγιο {{code}};\nΌσα αιτήματα δεν έχουν συλλεχθεί θα απελευθερωθούν για επαναδρομολόγηση.',
        { code: route.code },
      ),
    });

    if (!confirmed) return; // user pressed Cancel / X

    /* 2️⃣ Build mutation payload exactly as before */
    const mutValues: any = Lo.pick(route, Lo.keys(new FormInput()));
    if (mutValues.routesLines) {
      mutValues.routesLines = mutValues.routesLines.map((r) =>
        Lo.pick(r, Lo.keys(new RouteLineInput())),
      );
    }

    /* 3️⃣ Fire mutation */
    try {
      await updateRoute({ variables: { data: mutValues } });
      toastSuccess(t('Το Δρομολόγιο ολοκληρώθηκε: {{code}}', { code: route.code }));
    } catch (error) {
      handleGraphqlError('mutation-complete-route', error);
    } finally {
      window.location.reload(); // Reload the page to reflect changes
    }
  };

  return (
    <Button
      onClick={handleCompleteRoute}
      disabled={loading || disabled}
      className="button-animate-scale"
      type="button"
      size="sm"
      variant="outline-success"
    >
      {loading ? (
        <Spinner animation="border" size="sm" className="mb-1" />
      ) : (
        <span className="d-flex align-items-center">
          <FaCheck className="me-2 button-icon" />
          {t('Ολοκλήρωση Δρομολογίου')}
        </span>
      )}
    </Button>
  );
};

export default RouteCompleteButton;
